import { useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import '../styles/LoginModal.css';

interface LoginModalProps {
  onClose: () => void;
}

export default function LoginModal({ onClose }: LoginModalProps) {
  const {
    signInWithGoogle,
    signInWithX,
    sendEmailSignInLink,
    completeEmailSignInLink,
    emailLinkPrompt,
  } = useAuth();
  const { t } = useGlossary();
  const [email, setEmail] = useState(() => {
    try {
      return window.localStorage.getItem('emailForSignIn') ?? '';
    } catch {
      return '';
    }
  });
  const [sending, setSending] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const emailMode = useMemo(() => emailLinkPrompt, [emailLinkPrompt]);

  const handleGoogle = async () => {
    try {
      await signInWithGoogle();
      onClose();
    } catch (err) {
      console.error('Google sign-in failed:', err);
    }
  };

  const handleX = async () => {
    try {
      await signInWithX();
      onClose();
    } catch (err) {
      console.error('X sign-in failed:', err);
    }
  };

  const handleSendLink = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('login.email.error'));
      return;
    }
    setSending(true);
    setError('');
    try {
      await sendEmailSignInLink(trimmed);
      setMessage(t('login.email.sent'));
    } catch (err) {
      console.error('Email link sign-in failed:', err);
      setError(t('login.email.error'));
    } finally {
      setSending(false);
    }
  };

  const handleCompleteLink = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setError(t('login.email.confirmError'));
      return;
    }
    setCompleting(true);
    setError('');
    try {
      await completeEmailSignInLink(trimmed);
      onClose();
    } catch (err) {
      console.error('Email link completion failed:', err);
      setError(t('login.email.confirmError'));
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className="lm-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('login.aria')}>
        <button className="lm-close" onClick={onClose} aria-label={t('login.close')}>✕</button>

        <div className="lm-logo">🦅</div>
        <h2 className="lm-title">{t('login.title')}</h2>
        <p className="lm-subtitle">{t('login.subtitle')}</p>

        <div className="lm-email-panel">
          <div className="lm-email-title">
            {emailMode ? t('login.email.completeTitle') : t('login.email.title')}
          </div>
          <p className="lm-email-copy">
            {emailMode ? t('login.email.completeCopy') : t('login.email.copy')}
          </p>
          <label className="lm-email-label">
            {t('login.email.label')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('login.email.placeholder')}
              autoComplete="email"
              inputMode="email"
            />
          </label>
          {emailMode ? (
            <button className="lm-email-btn lm-email-btn--primary" onClick={handleCompleteLink} disabled={completing}>
              {completing ? t('login.email.processing') : t('login.email.complete')}
            </button>
          ) : (
            <button className="lm-email-btn lm-email-btn--primary" onClick={handleSendLink} disabled={sending}>
              {sending ? t('login.email.processing') : t('login.email.send')}
            </button>
          )}
          {message && <p className="lm-success">{message}</p>}
          {error && <p className="lm-error">{error}</p>}
          <p className="lm-note">{emailMode ? t('login.email.instructionsComplete') : t('login.email.instructions')}</p>
        </div>

        <div className="lm-divider">{t('login.or')}</div>

        <button className="lm-google-btn" onClick={handleGoogle}>
          <svg className="lm-google-icon" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          {t('login.google')}
        </button>

        <button className="lm-x-btn" onClick={handleX}>
          <svg className="lm-x-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25l-7.165 8.159L3.75 2.25H0l8.105 11.78L0 21.75h3.75l6.88-7.925L18.244 21.75H24l-8.1-11.78L24 2.25h-5.756z" fill="currentColor" />
          </svg>
          {t('login.x')}
        </button>

        <p className="lm-note">{t('login.note')}</p>
      </div>
    </div>
  );
}
